import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Search, X, User, LogOut, Store, LayoutDashboard, Package, MessageCircle, Heart } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";

const ADMIN_WA = "33777369462";

export default function Header() {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const [search, setSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/catalogue?search=${encodeURIComponent(search.trim())}`);
      setSearch("");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
    setDropdownOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
      {/* Main header */}
      <div className="w-full max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
        {/* Logo officiel */}
        <Link to="/" className="shrink-0 font-black text-xl text-[#16A34A]" data-testid="logo-link" aria-label="Jokkoleen — Accueil">
          Jokkoleen
        </Link>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="flex-1 flex min-w-0" data-testid="search-form">
          <input
            type="text"
            placeholder="Quel service ou produit cherchez-vous ?"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 px-4 py-2.5 rounded-l-2xl text-sm text-gray-900 bg-gray-100 hover:bg-gray-200 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#16A34A] border border-gray-200 border-r-0 transition-colors min-w-0"
            data-testid="search-input"
          />
          <button
            type="submit"
            className="bg-[#F59E0B] hover:bg-[#D97706] text-white px-4 py-2.5 rounded-r-2xl transition-colors border border-[#F59E0B]"
            data-testid="search-button"
          >
            <Search size={16} />
          </button>
        </form>

        {/* Favorites */}
        <Link to="/favoris" className="hidden sm:flex relative p-2 text-gray-500 hover:text-red-500 transition-colors" data-testid="favorites-link" aria-label="Mes favoris">
          <Heart size={20} />
        </Link>

        {/* Messages (vendor only) */}
        {user?.role === "vendor" && (
          <Link to="/messages" className="hidden sm:flex relative p-2 text-gray-500 hover:text-green-600 transition-colors" data-testid="messages-link" aria-label="Messages">
            <MessageCircle size={20} />
          </Link>
        )}

        {/* Cart */}
        <Link to="/panier" className="relative p-2 text-gray-600 hover:text-green-600 transition-colors" data-testid="cart-link">
          <ShoppingCart size={22} />
          {cartCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-green-600 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" data-testid="cart-count">
              {cartCount > 9 ? "9+" : cartCount}
            </span>
          )}
        </Link>

        {/* WhatsApp Admin */}
        <a
          href={`https://wa.me/${ADMIN_WA}`}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden sm:flex items-center gap-1.5 bg-[#25D366] text-white text-xs font-bold px-3 py-2 rounded-2xl hover:bg-[#20BD5A] transition-colors"
          data-testid="header-whatsapp"
        >
          <MessageCircle size={14} /> Aide
        </a>

        {/* User menu */}
        {user ? (
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1.5 text-gray-700 hover:text-green-600 transition-colors p-1"
              data-testid="user-menu-button"
            >
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center border-2 border-green-200">
                <span className="text-xs font-extrabold text-green-700">{user.name?.[0]?.toUpperCase()}</span>
              </div>
              <span className="hidden sm:block text-sm font-semibold truncate max-w-[80px]">{user.name?.split(" ")[0]}</span>
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                <div className="px-4 py-3 bg-green-50 border-b border-green-100">
                  <p className="text-xs font-bold text-green-800">{user.name}</p>
                  <p className="text-xs text-green-600">{user.whatsapp || user.email}</p>
                </div>
                {user.role === "vendor" && (
                  <>
                    <Link to="/dashboard/vendeur" className="flex items-center gap-2.5 px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors" onClick={() => setDropdownOpen(false)} data-testid="vendor-dashboard-link">
                      <Store size={15} /> Ma boutique
                    </Link>
                    <Link to="/messages" className="flex items-center gap-2.5 px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors" onClick={() => setDropdownOpen(false)} data-testid="messages-menu-link">
                      <MessageCircle size={15} /> Messages
                    </Link>
                  </>
                )}
                {user.role === "admin" && (
                  <Link to="/dashboard/admin" className="flex items-center gap-2.5 px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors" onClick={() => setDropdownOpen(false)} data-testid="admin-dashboard-link">
                    <LayoutDashboard size={15} /> Administration
                  </Link>
                )}
                <Link to="/favoris" className="flex items-center gap-2.5 px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors" onClick={() => setDropdownOpen(false)} data-testid="favorites-menu-link">
                  <Heart size={15} /> Mes favoris
                </Link>
                <Link to="/commandes" className="flex items-center gap-2.5 px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors" onClick={() => setDropdownOpen(false)} data-testid="orders-link">
                  <Package size={15} /> Mes commandes
                </Link>
                <Link to="/profil" className="flex items-center gap-2.5 px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors" onClick={() => setDropdownOpen(false)} data-testid="profile-link">
                  <User size={15} /> Mon profil
                </Link>
                <div className="border-t border-gray-100">
                  <button onClick={handleLogout} className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-red-500 hover:bg-red-50 transition-colors" data-testid="logout-button">
                    <LogOut size={15} /> Déconnexion
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <Link
            to="/auth"
            className="bg-[#16A34A] text-white text-sm font-bold px-4 py-2.5 rounded-2xl hover:bg-[#166534] transition-colors whitespace-nowrap flex items-center gap-1.5"
            data-testid="login-button"
          >
            <Store size={15} /> Espace vendeur
          </Link>
        )}
      </div>

      {/* Category nav */}
      <div className="bg-[#16A34A] hidden sm:block">
        <div className="w-full max-w-6xl mx-auto px-4 py-1.5 flex items-center gap-5 text-xs text-green-100">
          <Link to="/catalogue" className="hover:text-white transition-colors font-semibold" data-testid="all-products-link">Tous les produits</Link>
          <Link to="/catalogue?category=electronique" className="hover:text-white transition-colors">Électronique</Link>
          <Link to="/catalogue?category=mode-vetements" className="hover:text-white transition-colors">Mode</Link>
          <Link to="/catalogue?category=alimentation" className="hover:text-white transition-colors">Alimentation</Link>
          <Link to="/catalogue?category=services" className="hover:text-white transition-colors">Services</Link>
          <Link to="/catalogue?category=maison-decoration" className="hover:text-white transition-colors">Maison</Link>
          <Link to="/favoris" className="hover:text-white transition-colors ml-auto flex items-center gap-1">
            <Heart size={11} /> Favoris
          </Link>
        </div>
      </div>

      {dropdownOpen && <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)} />}
    </header>
  );
}
